import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usersApi, authApi, directoryApi, vehiclesApi, settingsApi, postcodeApi, ApiError, type UserProfile, type CorporateAccount } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { LoadingBlock, Spinner } from "@/components/ui/spinner";
import { CameraCaptureDialog } from "@/components/parcelio/camera-capture-dialog";
import { Camera, LogOut, Mail, MapPin, Phone, ShieldCheck, Upload } from "lucide-react";
import { ZoneMappingDialog } from "@/components/parcelio/zone-mapping-dialog";

const kindLabel: Record<string, string> = {
  staff: "Staff",
  corporate: "Corporate account",
  personal: "Personal account",
  client: "Client account",
};

export default function Settings() {
  const session = useAuth((s) => s.session);
  const clear = useAuth((s) => s.clear);
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [corp, setCorp] = useState<CorporateAccount | null>(null);
  const [vehicleCount, setVehicleCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "" });
  const isAdmin = session?.kind === "staff" && session.roles.some((r) => r === "admin" || r === "superadmin");
  const [locationSearchEnabled, setLocationSearchEnabled] = useState(true);
  const [savingLocationSearch, setSavingLocationSearch] = useState(false);
  const [postcodeEnrichmentEnabled, setPostcodeEnrichmentEnabled] = useState(false);
  const [savingPostcodeEnrichment, setSavingPostcodeEnrichment] = useState(false);
  const [checkingPostcodeStatus, setCheckingPostcodeStatus] = useState(false);
  const [zoneRates, setZoneRates] = useState<Array<{ zone: string; fee_multiplier: number; label: string | null }>>([]);
  const [zoneRateDrafts, setZoneRateDrafts] = useState<Record<string, string>>({});
  const [newZone, setNewZone] = useState("");
  const [newZoneMultiplier, setNewZoneMultiplier] = useState("1.0");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCameraOpen, setPhotoCameraOpen] = useState(false);
  const photoFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!session || !isAdmin) return;
    settingsApi
      .get(session.accessToken)
      .then((s) => {
        setLocationSearchEnabled(s.location_search_enabled !== false);
        setPostcodeEnrichmentEnabled(s.postcode_enrichment_enabled === true);
        if (s.postcode_enrichment_enabled === true) {
          postcodeApi.zoneRates(session.accessToken).then(setZoneRates).catch(() => {});
        }
      })
      .catch(() => {
        // best-effort — toggle defaults to on if settings fail to load
      });
  }, [session, isAdmin]);

  const saveZoneRate = async (zone: string) => {
    if (!session) return;
    const raw = zoneRateDrafts[zone];
    const multiplier = raw !== undefined ? parseFloat(raw) : zoneRates.find((r) => r.zone === zone)?.fee_multiplier;
    if (!multiplier || Number.isNaN(multiplier) || multiplier <= 0) {
      toast.error("Enter a valid multiplier");
      return;
    }
    try {
      await postcodeApi.setZoneRate(zone, multiplier, null, session.accessToken);
      setZoneRates((prev) => prev.map((r) => (r.zone === zone ? { ...r, fee_multiplier: multiplier } : r)));
      toast.success(`Zone ${zone} multiplier updated`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not save zone rate");
    }
  };

  const addZoneRate = async () => {
    if (!session) return;
    const zone = newZone.trim().toUpperCase();
    const multiplier = parseFloat(newZoneMultiplier);
    if (!zone) {
      toast.error("Enter a zone code");
      return;
    }
    if (!multiplier || Number.isNaN(multiplier) || multiplier <= 0) {
      toast.error("Enter a valid multiplier");
      return;
    }
    try {
      await postcodeApi.setZoneRate(zone, multiplier, null, session.accessToken);
      setZoneRates((prev) => [...prev.filter((r) => r.zone !== zone), { zone, fee_multiplier: multiplier, label: null }].sort((a, b) => a.zone.localeCompare(b.zone)));
      setNewZone("");
      setNewZoneMultiplier("1.0");
      toast.success(`Zone ${zone} added`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not add zone rate");
    }
  };

  const toggleLocationSearch = async () => {
    if (!session) return;
    const next = !locationSearchEnabled;
    setSavingLocationSearch(true);
    try {
      await settingsApi.setLocationSearch(next, session.accessToken);
      setLocationSearchEnabled(next);
      toast.success(`Map location search ${next ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not update setting");
    } finally {
      setSavingLocationSearch(false);
    }
  };

  const togglePostcodeEnrichment = async () => {
    if (!session) return;
    const next = !postcodeEnrichmentEnabled;
    setSavingPostcodeEnrichment(true);
    try {
      await settingsApi.setPostcodeEnrichment(next, session.accessToken);
      setPostcodeEnrichmentEnabled(next);
      toast.success(`Postcode verification (NIPOST) ${next ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not update setting");
    } finally {
      setSavingPostcodeEnrichment(false);
    }
  };

  const checkPostcodeStatus = async () => {
    if (!session) return;
    setCheckingPostcodeStatus(true);
    try {
      const s = await settingsApi.postcodeStatus(session.accessToken);
      if (s.active) {
        toast.success("Postcode enrichment is live — key valid, toggle on.");
      } else if (!s.key_configured) {
        toast.error("No API key loaded on this server — set POSTCODE_API_KEY in the live backend .env and restart.");
      } else if (s.key_valid === false) {
        toast.error(`API key rejected by NIPOST: ${s.error ?? "unknown error"}`);
      } else if (!s.toggle_enabled) {
        toast.error("Key is valid but the toggle above is off.");
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not check postcode status");
    } finally {
      setCheckingPostcodeStatus(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await usersApi.get(session.userId, session.accessToken);
        if (cancelled) return;
        setProfile(p);
        setForm({ full_name: p.full_name ?? "", phone: p.phone ?? "", email: p.email ?? "" });

        if (session.kind === "corporate") {
          // /corporates (full directory) is staff-only server-side — this
          // resolves the caller's own company instead, which actually works
          // for a real corporate login (see Dashboard.tsx's CorporateHome).
          const mine = await directoryApi.myCorporate(session.accessToken).catch(() => null);
          if (!cancelled) setCorp(mine);
        }
        if (session.kind === "personal") {
          const vehicles = await vehiclesApi.list(session.accessToken);
          if (!cancelled) setVehicleCount(vehicles.filter((v) => v.owner_id === session.userId).length);
        }
      } catch {
        if (!cancelled) toast.error("Could not load your profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const saveProfile = async () => {
    if (!session || !profile) return;
    setSaving(true);
    try {
      const body: Partial<{ full_name: string; phone: string; email: string }> = {};
      if (form.full_name.trim() !== (profile.full_name ?? "")) body.full_name = form.full_name.trim();
      if (form.phone.trim() !== (profile.phone ?? "")) body.phone = form.phone.trim();
      const emailChanged = form.email.trim().toLowerCase() !== (profile.email ?? "").toLowerCase();
      if (emailChanged) body.email = form.email.trim();

      if (Object.keys(body).length === 0) {
        setEditing(false);
        return;
      }

      const updated = await usersApi.update(session.userId, body, session.accessToken);
      setProfile((prev) => (prev ? { ...prev, full_name: updated.full_name, phone: updated.phone } : prev));
      setForm((f) => ({ ...f, email: profile.email ?? "" })); // email stays unchanged until verified

      if (emailChanged) {
        toast.success(`Verification link sent to ${form.email.trim()} — confirm it to finish changing your email`);
      } else {
        toast.success("Profile updated");
      }
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not update your profile");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file: File | undefined) => {
    if (!file || !session) return;
    setUploadingPhoto(true);
    try {
      const updated = await usersApi.uploadPhoto(session.userId, file, session.accessToken);
      setProfile((prev) => (prev ? { ...prev, photo_url: updated.photo_url } : prev));
      toast.success("Profile photo updated");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const signOut = async () => {
    if (session) {
      try {
        await authApi.logout(session.refreshToken);
      } catch {
        // best-effort — clear local session regardless
      }
    }
    clear();
    navigate("/auth/login", { replace: true });
  };

  if (!session) return null;

  return (
    <>
      <Topbar title="Settings" subtitle="Your account" />
      <div className="flex-1 p-4 md:p-6">
        <div className="mx-auto grid max-w-2xl gap-6">
          {loading ? (
            <LoadingBlock label="Loading your profile…" />
          ) : (
            <section className="surface-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Profile</h3>
                <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium">
                  <ShieldCheck className="h-3 w-3" /> {kindLabel[session.kind] ?? session.kind}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {profile?.photo_url ? <AvatarImage src={profile.photo_url} alt={profile.full_name} /> : null}
                  <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                    {(profile?.full_name ?? "U")
                      .trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((p) => p[0])
                      .join("")
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex gap-2">
                  <input
                    ref={photoFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                  <Button type="button" size="sm" variant="outline" disabled={uploadingPhoto} onClick={() => photoFileRef.current?.click()}>
                    {uploadingPhoto ? <Spinner className="mr-1.5" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                    Upload
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={uploadingPhoto} onClick={() => setPhotoCameraOpen(true)}>
                    <Camera className="mr-1.5 h-3.5 w-3.5" />
                    Camera
                  </Button>
                </div>
              </div>
              <CameraCaptureDialog
                open={photoCameraOpen}
                onOpenChange={setPhotoCameraOpen}
                onCapture={uploadPhoto}
                facingMode="user"
              />
              <div className="mt-4 space-y-3">
                {editing ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="full_name">Name</Label>
                      <Input
                        id="full_name"
                        value={form.full_name}
                        onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Changing this sends a confirmation link to the new address — it only takes effect once you confirm it.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={saveProfile} disabled={saving}>
                        {saving ? "Saving…" : "Save changes"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving}
                        onClick={() => {
                          setForm({ full_name: profile?.full_name ?? "", phone: profile?.phone ?? "", email: profile?.email ?? "" });
                          setEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="text-xs text-muted-foreground">Name</div>
                      <div className="text-sm font-medium">{profile?.full_name ?? "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      <div className="text-sm">{profile?.email ?? "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                      <div className="text-sm">{profile?.phone ?? "—"}</div>
                    </div>
                    <div className="pt-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                        Edit details
                      </Button>
                    </div>
                  </>
                )}
                {session.kind === "staff" && session.roles.length > 0 ? (
                  <div>
                    <div className="text-xs text-muted-foreground">Roles</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {session.roles.map((r) => (
                        <span key={r} className="rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize">{r.replace("_", " ")}</span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {session.kind === "corporate" && corp ? (
                  <div>
                    <div className="text-xs text-muted-foreground">Company</div>
                    <div className="text-sm font-medium">{corp.company}</div>
                  </div>
                ) : null}
                {session.kind === "personal" && vehicleCount !== null ? (
                  <div>
                    <div className="text-xs text-muted-foreground">Vehicles</div>
                    <div className="text-sm font-medium">{vehicleCount}</div>
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {isAdmin ? (
            <section className="surface-card p-6">
              <h3 className="text-sm font-semibold">App settings</h3>
              <p className="mt-1 text-xs text-muted-foreground">Admin-only controls.</p>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Map location search</div>
                    <p className="text-[11px] text-muted-foreground">
                      Lets operators search for a place on the vehicle tracking map. Each query spends geocoding
                      provider credit — turn off to stop that spend.
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={savingLocationSearch} onClick={toggleLocationSearch}>
                  {locationSearchEnabled ? "Enabled" : "Disabled"}
                </Button>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t pt-4">
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Postcode verification (NIPOST)</div>
                    <p className="text-[11px] text-muted-foreground">
                      Enriches pickup/dropoff addresses with an official NIPOST postcode after geocoding, and enables
                      the postcode lookup/autocomplete input on deliveries. Requires a configured API key.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" disabled={checkingPostcodeStatus} onClick={checkPostcodeStatus}>
                    {checkingPostcodeStatus ? "Checking…" : "Check status"}
                  </Button>
                  <Button size="sm" variant="outline" disabled={savingPostcodeEnrichment} onClick={togglePostcodeEnrichment}>
                    {postcodeEnrichmentEnabled ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              </div>
              {postcodeEnrichmentEnabled ? (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    Zone fee multipliers (demo)
                    <ZoneMappingDialog />
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Suggested pricing multiplier per NIPOST zone — informational only, shown as a badge during delivery
                    creation. Does not change the amount actually charged.
                  </p>
                  <div className="mt-3 space-y-2">
                    {zoneRates.map((r) => (
                      <div key={r.zone} className="flex items-center gap-2 text-xs">
                        <span className="w-12 font-mono font-medium">{r.zone}</span>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.1"
                          className="h-7 w-20 text-xs"
                          value={zoneRateDrafts[r.zone] ?? String(r.fee_multiplier)}
                          onChange={(e) => setZoneRateDrafts((prev) => ({ ...prev, [r.zone]: e.target.value }))}
                        />
                        <span className="text-muted-foreground">×</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => saveZoneRate(r.zone)}
                        >
                          Save
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-1 text-xs">
                      <Input
                        placeholder="Zone (e.g. LA)"
                        className="h-7 w-20 text-xs uppercase"
                        value={newZone}
                        onChange={(e) => setNewZone(e.target.value)}
                      />
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        placeholder="1.0"
                        className="h-7 w-20 text-xs"
                        value={newZoneMultiplier}
                        onChange={(e) => setNewZoneMultiplier(e.target.value)}
                      />
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addZoneRate}>
                        Add zone
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          <section className="surface-card p-6">
            <h3 className="text-sm font-semibold">Session</h3>
            <p className="mt-1 text-xs text-muted-foreground">Sign out of this device.</p>
            <div className="mt-4">
              <Button variant="outline" onClick={signOut}>
                <LogOut className="mr-1.5 h-4 w-4" /> Sign out
              </Button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
